import { useMutation } from "@apollo/client/react";
import { CREATE_AUTO_TEMPLATE } from "@graphql/mutations/AutoTemplates";
import EDIT_USER from "@graphql/mutations/EditUser";
import { useCurrentUser } from "@hooks/use-current-user";
import { TemplateType } from "@src/codegen/gql-schema";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";

import { getUrlParam } from "../components/Onboarding/utils/urlParams";

/* eslint-disable @typescript-eslint/no-explicit-any */

interface UseOnboardingActionsParams {
  step: string;
  navigate: (path: string, options?: { state?: any }) => void;
  goToStep: (step: string) => void;
  selectedRole: number | string | null;
  allowedRoles: { DVM: number; [key: string]: number };
  templateType: string | null;
  getValues: () => { templateFields?: any[] };
  handleCreateTeam: () => Promise<void>;
  handleAddUsers: () => Promise<void>;
  handleConfirmNewUsers: () => Promise<void>;
  navigateTeamFlow?: (step: string) => void;
  navigateFromRole?: (isVet: boolean) => void;
  invitedTeamName?: string | null;
  newUsersState?: any[];
  existingUsersState?: any[];
  referralSource?: string;
  clinicGroup?: string;
  refetchTemplates?: () => void;
}

interface HandleBackParams {
  goBack: () => void;
  setIsTemplateLoading: (value: boolean) => void;
}

export function useOnboardingActions({
  step,
  navigate,
  goToStep,
  selectedRole,
  allowedRoles,
  templateType,
  getValues,
  handleCreateTeam,
  handleAddUsers,
  handleConfirmNewUsers,
  navigateTeamFlow: _navigateTeamFlow,
  navigateFromRole,
  invitedTeamName,
  newUsersState,
  existingUsersState,
  referralSource,
  clinicGroup,
  refetchTemplates,
}: UseOnboardingActionsParams) {
  const [createAutoTemplate] = useMutation(CREATE_AUTO_TEMPLATE);
  const [editUser] = useMutation(EDIT_USER);
  const { refetch: refetchCurrentUser } = useCurrentUser();
  const { releaseWebEnableFtueTour } = useFlags();

  const handleNext = async () => {
    let nextStep = null;

    switch (step) {
      case "welcome": {
        // If role is already set from a team invitation, skip role selection
        if (selectedRole && invitedTeamName) {
          mixpanel.track("Onboarding: Step Navigation", {
            from: "welcome",
            to:
              selectedRole === allowedRoles.DVM
                ? "template-choice"
                : "finish",
            action: "team_invite_skip",
            fromTeamInvite: true,
            role:
              selectedRole === allowedRoles.DVM ? "DVM" : "Non-DVM",
          });

          if (Number(selectedRole) === allowedRoles.DVM) {
            nextStep = "template-choice";
          } else {
            nextStep = "finish";
          }
        } else {
          nextStep = "role";
        }
        break;
      }
      case "role": {
        if (selectedRole) {
          try {
            await editUser({
              variables: {
                roleId: Number(selectedRole),
              },
            });
          } catch (e) {
            console.error("Failed to save user role", e);
          }
        }

        if (navigateFromRole) {
          navigateFromRole(Number(selectedRole) === allowedRoles.DVM);
        }
        if (Number(selectedRole) === allowedRoles.DVM) {
          if (releaseWebEnableFtueTour) {
            nextStep = "referral-source";
          } else {
            nextStep = "template-choice";
          }
        } else {
          if (invitedTeamName) {
            nextStep = "finish";
          } else {
            nextStep = "team-setup-choice";
          }
        }
        break;
      }

      case "template-choice": {
        if (templateType === "soap" || templateType === "non-soap") {
          nextStep = "template-setup";
        } else if (templateType === "scribenote") {
          nextStep = "cod-setup";
        } else {
          nextStep = "cod-setup";
        }
        break;
      }

      case "template-setup": {
        const values = getValues();
        if (values.templateFields?.length) {
          try {
            await createAutoTemplate({
              variables: {
                createTemplateInput: {
                  name: "Template from Onboarding",
                  description:
                    "This is the template generated from the onboarding. You can edit any of these values at any time.",
                  templateType:
                    templateType === "soap"
                      ? TemplateType.Soap
                      : TemplateType.Custom,
                  templateFields: values.templateFields,
                  isFavourite: true,
                },
              },
            });
            if (refetchTemplates) {
              refetchTemplates();
            }
          } catch (e) {
            console.error("Failed to save onboarding template", e);
            mixpanel.track("Onboarding: Failed to save template", {
              error: e instanceof Error ? e.message : "Unknown error",
            });
          }
        }
        if (invitedTeamName) {
          nextStep = "finish";
        } else {
          nextStep = "referral-source";
        }
        break;
      }

      case "cod-setup": {
        if (invitedTeamName) {
          nextStep = "finish";
        } else {
          nextStep = "referral-source";
        }
        break;
      }

      case "team-setup-choice": {
        nextStep = "referral-source";
        break;
      }

      case "team-create": {
        await handleCreateTeam();
        nextStep = "team-add-users";
        break;
      }

      case "team-add-users": {
        await handleAddUsers();
        nextStep = "team-confirm";
        break;
      }

      case "team-confirm": {
        const hasUsers =
          (newUsersState?.length ?? 0) > 0 ||
          (existingUsersState?.length ?? 0) > 0;

        if (hasUsers) {
          await handleConfirmNewUsers();
          nextStep = "team-receipt";
        } else {
          nextStep = "referral-source";
        }
        break;
      }

      case "team-receipt": {
        nextStep = "referral-source";
        break;
      }

      case "referral-source": {
        nextStep = "group-name";
        break;
      }

      case "group-name": {
        nextStep = "finish";
        break;
      }

      case "finish": {
        try {
          await editUser({
            variables: {
              isOnboardingComplete: true,
              onboardingReferralSource: referralSource,
              onboardingCorporateGroup: clinicGroup,
            },
          });

          await refetchCurrentUser();

          mixpanel.track("Onboarding: Step Navigation", {
            from: "finish",
            to: "dashboard",
            action: "onboarding_completed",
            onboardingCompleted: true,
            hasTeam: !!invitedTeamName,
            referralSource: referralSource,
            clinicGroup: clinicGroup,
          });
          navigate("/dashboard", {
            state: { fromOnboarding: true },
          });
        } catch (e) {
          console.error("Failed to mark onboarding as complete", e);
          mixpanel.track(
            "Onboarding: Failed to mark onboarding as complete",
            {
              error: e instanceof Error ? e.message : "Unknown error",
            },
          );
        }
        break;
      }

      default: {
        console.warn(`Unhandled step: ${step}`);
      }
    }

    if (nextStep) {
      const additionalInfo: Record<string, any> = {};

      switch (step) {
        case "welcome":
          break;
        case "role":
          additionalInfo.role =
            selectedRole === allowedRoles.DVM ? "DVM" : "Non-DVM";
          break;
        case "template-choice":
          additionalInfo.templateChoice = templateType || "default";
          break;
        case "template-setup": {
          const values = getValues();
          additionalInfo.templateCreated =
            (values.templateFields?.length ?? 0) > 0;
          additionalInfo.templateType =
            templateType === "soap" ? "SOAP" : "CUSTOM";
          break;
        }
        case "cod-setup": {
          additionalInfo.codSetupCompleted = true;
          break;
        }
        case "team-setup-choice": {
          break;
        }
        case "team-create": {
          additionalInfo.teamCreationStarted = true;
          break;
        }
        case "team-confirm": {
          const hasUsers =
            (newUsersState?.length ?? 0) > 0 ||
            (existingUsersState?.length ?? 0) > 0;
          additionalInfo.teamCreated = true;
          additionalInfo.teamHasUsers = hasUsers;
          if (hasUsers) {
            additionalInfo.userCount =
              (newUsersState?.length ?? 0) +
              (existingUsersState?.length ?? 0);
          }
          break;
        }
        case "referral-source":
          additionalInfo.referralSource = referralSource;
          break;
        case "group-name":
          additionalInfo.clinicGroup = clinicGroup;
          break;
        case "finish":
          additionalInfo.onboardingCompleted = true;
          additionalInfo.hasTeam = !!invitedTeamName;
          additionalInfo.referralSource = referralSource;
          additionalInfo.clinicGroup = clinicGroup;
          break;
      }

      mixpanel.track("Onboarding: Step Navigation", {
        from: step,
        to: nextStep,
        ...additionalInfo,
      });
      goToStep(nextStep);
    }
  };

  const handleBack = ({
    goBack,
    setIsTemplateLoading,
  }: HandleBackParams) => {
    if (step === "template-setup") {
      setIsTemplateLoading(false);
    }
    goBack();
  };

  return {
    handleNext,
    handleBack,
    getUrlParam,
  };
}
